/**
 * CaptureImagePanel — floating popup card for survey capture viewer.
 *
 * @remarks
 * Overlaid absolutely on the map (bottom-right).
 * Shows image with click-to-mark overlay (Fase 5.2), compact metadata,
 * navigation, close button, and a collapsible observation area (Fase 4).
 * Opened/closed by parent (CaptureViewer).
 */

import { useState } from 'react';
import { useSurveyObservations } from '../hooks/useSurveyObservations';
import { getCaptureImageUrl } from '../services/survey-api.service';
import type {
  CreateObservationPayload,
  SurveyCapture,
  UpdateObservationPayload,
} from '../types/survey.types';
import { estimatePositionFromImageClick } from '../utils/spatial';
import { ImageClickOverlay } from './ImageClickOverlay';
import { ObservationForm } from './ObservationForm';
import { ObservationList } from './ObservationList';

interface CaptureImagePanelProps {
  capture: SurveyCapture | null;
  sessionId: string;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function formatHeading(deg: number | null): string {
  if (deg === null) return '—';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  const label = dirs[Math.round(deg / 45) % 8];
  return `${deg.toFixed(0)}° ${label}`;
}

// ── Capture metadata — extracted to keep CaptureImagePanel under line limit ───

interface CaptureMetaProps {
  capture: SurveyCapture;
}

function CaptureMeta({ capture }: CaptureMetaProps): JSX.Element {
  return (
    <div className="sv-popup-meta">
      <div className="sv-popup-meta-item">
        <span className="sv-popup-meta-label">Índice</span>
        <span className="sv-popup-meta-value">#{capture.sequenceIndex}</span>
      </div>
      <div className="sv-popup-meta-item">
        <span className="sv-popup-meta-label">Distancia</span>
        <span className="sv-popup-meta-value">{capture.distanceFromStartM.toFixed(0)} m</span>
      </div>
      <div className="sv-popup-meta-item">
        <span className="sv-popup-meta-label">Heading</span>
        <span className="sv-popup-meta-value">{formatHeading(capture.cameraHeading)}</span>
      </div>
      <div className="sv-popup-meta-item">
        <span className="sv-popup-meta-label">Lat / Lon</span>
        <span className="sv-popup-meta-value">
          {capture.cameraLat.toFixed(5)}, {capture.cameraLon.toFixed(5)}
        </span>
      </div>
      <div className="sv-popup-meta-item sv-popup-meta-item--full">
        <span className="sv-popup-meta-label">Timestamp</span>
        <span className="sv-popup-meta-value">{formatTimestamp(capture.captureTimestamp)}</span>
      </div>
      {capture.gpsInterpolated && (
        <div className="sv-popup-meta-item sv-popup-meta-item--full">
          <span className="sv-popup-meta-value sv-popup-meta-interpolated">GPS interpolado</span>
        </div>
      )}
    </div>
  );
}

// ── Observation area — isolated to keep panel under 100 lines ─────────────────

interface ObservationAreaProps {
  capture: SurveyCapture;
  sessionId: string;
  observations: ReturnType<typeof useSurveyObservations>['observations'];
  isLoading: boolean;
  markingObsId: string | null;
  onStartMark: (id: string) => void;
  create: (payload: CreateObservationPayload) => Promise<void>;
  confirm: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

function ObservationArea({
  capture,
  sessionId,
  observations,
  isLoading,
  markingObsId,
  onStartMark,
  create,
  confirm,
  remove,
}: ObservationAreaProps): JSX.Element {
  const [showForm, setShowForm] = useState(false);

  async function handleCreated(payload: Parameters<typeof create>[0]): Promise<void> {
    await create(payload);
    setShowForm(false);
  }

  return (
    <div className="sv-obs-area">
      <div className="sv-obs-area-header">
        <span className="sv-obs-area-title">Observaciones</span>
        {!showForm && (
          <button type="button" className="sv-obs-area-add-btn" onClick={() => setShowForm(true)}>
            + Observación
          </button>
        )}
      </div>
      {showForm && (
        <ObservationForm
          sessionId={sessionId}
          captureId={capture.id}
          defaultLat={capture.cameraLat}
          defaultLon={capture.cameraLon}
          onCreated={handleCreated}
          onCancel={() => setShowForm(false)}
        />
      )}
      <ObservationList
        observations={observations}
        isLoading={isLoading}
        markingObsId={markingObsId}
        onConfirm={id => void confirm(id)}
        onRemove={id => void remove(id)}
        onMark={onStartMark}
      />
    </div>
  );
}

// ── Placement payload builder — extracted to keep CaptureImagePanel under 100L ─

function buildPlacementPayload(
  imageX: number,
  imageY: number,
  naturalWidth: number,
  capture: SurveyCapture
): UpdateObservationPayload {
  const pos = estimatePositionFromImageClick(
    capture.cameraLat,
    capture.cameraLon,
    capture.cameraHeading,
    imageX,
    naturalWidth
  );
  return {
    imageX,
    imageY,
    placementMethod: 'manual_click',
    estimatedLat: pos.estimatedLat,
    estimatedLon: pos.estimatedLon,
    confidence: pos.confidence,
    manuallyAdjusted: false,
  };
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export function CaptureImagePanel({
  capture,
  sessionId,
  index,
  total,
  onPrev,
  onNext,
  onClose,
}: CaptureImagePanelProps): JSX.Element {
  const { observations, isLoading, create, update, confirm, remove } = useSurveyObservations(
    capture?.id ?? null
  );
  const [markingObsId, setMarkingObsId] = useState<string | null>(null);

  function handleImageClick(imageX: number, imageY: number, naturalWidth: number): void {
    if (!markingObsId || !capture) return;
    void update(markingObsId, buildPlacementPayload(imageX, imageY, naturalWidth, capture));
    setMarkingObsId(null);
  }

  const markedObs = markingObsId ? (observations.find(o => o.id === markingObsId) ?? null) : null;

  return (
    <div className="sv-popup">
      {/* Header: navigation + counter + close */}
      <div className="sv-popup-header">
        <div className="sv-popup-nav">
          <button
            type="button"
            className="sv-popup-nav-btn"
            disabled={index === 0}
            onClick={onPrev}
            aria-label="Captura anterior"
          >
            ‹
          </button>
          <span className="sv-popup-counter">{total > 0 ? `${index + 1} / ${total}` : '—'}</span>
          <button
            type="button"
            className="sv-popup-nav-btn"
            disabled={index >= total - 1}
            onClick={onNext}
            aria-label="Captura siguiente"
          >
            ›
          </button>
        </div>
        <button
          type="button"
          className="sv-popup-close"
          onClick={onClose}
          aria-label="Cerrar panel"
        >
          ✕
        </button>
      </div>

      {/* Image with click-to-mark overlay */}
      <div className="sv-popup-image-wrap">
        {capture ? (
          <ImageClickOverlay
            src={getCaptureImageUrl(capture.storageKey)}
            alt={`Captura ${capture.sequenceIndex}`}
            isMarking={markingObsId !== null}
            markedImageX={markedObs?.imageX ?? null}
            markedImageY={markedObs?.imageY ?? null}
            onImageClick={handleImageClick}
          />
        ) : (
          <div className="sv-popup-no-image">Sin imagen</div>
        )}
      </div>

      {/* Compact metadata */}
      {capture && <CaptureMeta capture={capture} />}

      {/* Observations */}
      {capture && (
        <ObservationArea
          capture={capture}
          sessionId={sessionId}
          observations={observations}
          isLoading={isLoading}
          markingObsId={markingObsId}
          onStartMark={setMarkingObsId}
          create={create}
          confirm={confirm}
          remove={remove}
        />
      )}
    </div>
  );
}
